import axios from 'axios';
import GetCookie from '../../utils/GetCookie';

export const RESPONSE_IMAGE_TYPES = ['image/png', 'image/jpeg'];
export const RESPONSE_IMAGE_MAX_MB = 10;

const EXT_BY_TYPE = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
};

// The first image file on a paste/drop, or null. Screenshots arrive as items with no
// name, so the item list is read as well as the file list.
export const imageFileFromTransfer = (dataTransfer) => {
  if (!dataTransfer) return null;
  const files = Array.from(dataTransfer.files || []);
  const fromFiles = files.find(f => RESPONSE_IMAGE_TYPES.includes(f.type));
  if (fromFiles) return fromFiles;
  const item = Array.from(dataTransfer.items || [])
    .find(it => it.kind === 'file' && RESPONSE_IMAGE_TYPES.includes(it.type));
  return item ? item.getAsFile() : null;
};

// Turn an <img src> (data: / blob: / http(s):) into a File. A cross-site image the
// browser is not allowed to read throws; the message says what to do instead.
const fileFromImageSrc = async (src) => {
  let blob;
  try {
    blob = await fetch(src).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    });
  } catch (e) {
    throw new Error('The copied image could not be read from its source. Take a screenshot of it (Win+Shift+S), or save it and drop the file here.');
  }
  if (!RESPONSE_IMAGE_TYPES.includes(blob.type)) {
    throw new Error('Only PNG or JPEG images can be pasted.');
  }
  return new File([blob], `pasted-image.${EXT_BY_TYPE[blob.type] || 'png'}`, { type: blob.type });
};

/**
 * What a paste/drop holds, as { load: () => Promise<File> }, or null when it is
 * not an image (the editor then pastes normally).
 *
 * Two shapes reach us: an image file (screenshots, "Copy image" in most browsers),
 * or only HTML holding an <img> (Teams, Outlook, Word, some browsers) — which the
 * editors would otherwise drop or paste as a raw <img>. HTML counts as an image
 * paste only when it carries one image and no text, so pasting a passage that
 * happens to contain a picture still pastes the text.
 */
export const imageSourceFromTransfer = (dataTransfer) => {
  if (!dataTransfer) return null;
  const file = imageFileFromTransfer(dataTransfer);
  if (file) return { load: async () => file };

  const html = typeof dataTransfer.getData === 'function' ? dataTransfer.getData('text/html') : '';
  if (!html || !/<img\b/i.test(html)) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const imgs = Array.from(doc.images);
  const text = (doc.body?.textContent || '').replace(/ /g, ' ').trim();
  if (imgs.length !== 1 || text) return null;
  const src = imgs[0].getAttribute('src');
  if (!src) return null;
  return { load: () => fileFromImageSrc(src) };
};

export const validateResponseImage = (file) => {
  if (!file) return 'No image found on the clipboard.';
  if (!RESPONSE_IMAGE_TYPES.includes(file.type)) return 'Only PNG or JPEG images can be pasted.';
  if (file.size > RESPONSE_IMAGE_MAX_MB * 1024 * 1024) return `Image is larger than ${RESPONSE_IMAGE_MAX_MB} MB.`;
  return null;
};

/**
 * Upload a pasted image into the agent's response_images folder, named like the
 * agent's own images (img_agent{a}_idx{n} / ipi_agent{a}_idx{n}).
 * placement: 'body' | 'cell'; minIndex: cell only, the editor's next free in-place index.
 * Returns { s3_key, preview_url, index }. The image becomes part of the response when
 * the edit is saved with Save Preset.
 */
export const uploadResponseImage = async ({
  file, templateId, fabricId, agentId, authToken, placement = 'body', minIndex = null,
}) => {
  const headers = { Authorization: authToken || GetCookie('access_token') };
  // The API checks the extension against the type, and pasted screenshots often
  // have no useful name, so the name always follows the type.
  const fileName = `pasted-image.${EXT_BY_TYPE[file.type] || 'png'}`;

  // The output bucket accepts no uploads from the browser, so the file goes through
  // the API, which writes it next to the agent's own images.
  const form = new FormData();
  form.append('template_id', templateId);
  form.append('fabric_id', fabricId);
  form.append('agent_id', agentId);
  form.append('placement', placement);
  if (minIndex != null) form.append('min_index', minIndex);
  form.append('image', file, fileName);

  const res = await axios.post('/pceapi/genbi/response-images/upload', form, { headers });
  if (res?.data?.status !== 'success' || !res.data.data?.s3_key) {
    throw new Error(res?.data?.message || 'The image could not be uploaded');
  }
  const { preview_url: previewUrl, s3_key: s3Key, index } = res.data.data;

  return { s3_key: s3Key, preview_url: previewUrl, index };
};

/**
 * Crops made in the editor live in the browser as PNG blobs until Save Preset. This
 * uploads them (through the same upload API) and returns the edit with each blob
 * swapped for the stored file's s3_key, ready for update-template.
 *
 * edited: an agent's editedResponse. Blobs sit on
 *   new_images[].crop_blob / new_in_place_images[].crop_blob  (pasted, then cropped)
 *   replaced_images[].blob / replaced_in_place_images[].blob  (stored figures, cropped)
 * A cropped in-place figure keeps its marker index; only its file changes.
 */
export const uploadCroppedImages = async (edited, { templateId, fabricId, agentId, authToken }) => {
  if (!edited) return edited;
  const upload = (blob, placement) => uploadResponseImage({
    file: new File([blob], 'cropped.png', { type: blob.type || 'image/png' }),
    templateId, fabricId, agentId, authToken, placement,
  });
  const swap = async (list, blobKey, placement) => Promise.all((list || []).map(async (item) => {
    const blob = item?.[blobKey];
    if (!blob) return item;
    const { s3_key } = await upload(blob, placement);
    const { [blobKey]: _drop, ...rest } = item;
    return { ...rest, s3_key };
  }));
  return {
    ...edited,
    new_images: await swap(edited.new_images, 'crop_blob', 'body'),
    new_in_place_images: await swap(edited.new_in_place_images, 'crop_blob', 'cell'),
    replaced_images: await swap(edited.replaced_images, 'blob', 'body'),
    replaced_in_place_images: await swap(edited.replaced_in_place_images, 'blob', 'cell'),
  };
};
